import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useTranslation } from "@/translations";

type ReportReason =
  | "other"
  | "explicitLanguage"
  | "spam"
  | "sexuallyExplicit"
  | "discrimination"
  | "bullying"
  | "impersonating"
  | "promotingViolence";

interface FormValues {
  reason: ReportReason;
  description?: string;
}

const MIN_LENGTH_CONTENT = 8;
const MAX_LENGTH_CONTENT = 500;
const FORM_ID = "report-post-modal-form";

const reportReasons: ReportReason[] = [
  "explicitLanguage",
  "spam",
  "sexuallyExplicit",
  "discrimination",
  "bullying",
  "impersonating",
  "promotingViolence",
  "other",
];

type ReportPostModalProps = ModalBaseProps & {
  messageId: string;
};

export function ReportPostModal({ messageId, isOpened, onOpenChange }: ReportPostModalProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<FormValues>();
  const reason = useWatch({ control: form.control, name: "reason" });
  const reportPostMutation = communityFeedMutations.useReportMessage(messageId);

  useEffect(() => {
    form.reset();
  }, [form, isOpened]);

  async function handleReport() {
    const formValues = form.getValues();

    await reportPostMutation.mutateAsync({
      reason: formValues.reason,
      description: formValues.description,
    });
    onOpenChange(false);
  }

  return (
    <Modal.Root
      size="sm"
      title={t("component.community-post.report.modal.title")}
      description={t("component.community-post.report.modal.description")}
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Form.Root id={FORM_ID} formMethods={form} onSubmit={handleReport}>
          <Stack gap="md">
            <FormField label={t("component.community-post.report.modal.input.reason.title")} required>
              <FormSelect<FormValues, ReportReason>
                name="reason"
                placeholder={t("component.community-post.report.modal.input.reason.placeholder")}
                items={reportReasons}
                keySelector={(x) => x}
                usePortal
                renderOption={(x) => {
                  switch (x) {
                    case "explicitLanguage":
                      return t("component.community-post.report.modal.options.explicit-language");
                    case "spam":
                      return t("component.community-post.report.modal.options.spam");
                    case "sexuallyExplicit":
                      return t("component.community-post.report.modal.options.sexually-explicit");
                    case "discrimination":
                      return t("component.community-post.report.modal.options.discrimination");
                    case "bullying":
                      return t("component.community-post.report.modal.options.bullying");
                    case "impersonating":
                      return t("component.community-post.report.modal.options.impersonating");
                    case "promotingViolence":
                      return t("component.community-post.report.modal.options.promoting-violence");
                    case "other":
                      return t("component.community-post.report.modal.options.other");
                  }
                }}
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("component.community-post.report.modal.input.reason.title"),
                  }),
                }}
              />
            </FormField>
            {reason === "other" && (
              <FormField label={t("component.community-post.report.modal.input.description.title")} required>
                <FormTextArea<FormValues>
                  name="description"
                  placeholder={t("component.community-post.report.modal.input.description.placeholder")}
                  rules={{
                    minLength: {
                      message: t("components.form.error.min-length", { length: MIN_LENGTH_CONTENT }),
                      value: MIN_LENGTH_CONTENT,
                    },
                    maxLength: {
                      message: t("components.form.error.max-length", { length: MAX_LENGTH_CONTENT }),
                      value: MAX_LENGTH_CONTENT,
                    },
                    validate: {
                      required: createRequiredStringRule(
                        t,
                        "component.community-post.report.modal.input.description.title",
                      ),
                    },
                  }}
                />
              </FormField>
            )}
          </Stack>
        </Form.Root>
      </Modal.Body>

      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" tone="primary">
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button data-testid="modal-report-button" form={FORM_ID} type="submit" isLoading={reportPostMutation.isPending}>
          {t("component.community-post.report.modal.report-button")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
